// Runs explorer (§11 `/runs`): filter by status/task, cursor pagination. Each run links to its
// detail (attempt timeline, response, callbacks). Status filter maps to the ?status= query param.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Api, type RunStatus } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { fmtDuration, fmtTime } from "../lib/format";
import { Empty, ErrorBox, Spinner, StatusBadge } from "../components/ui";

const STATUSES: Array<RunStatus | "all"> = [
  "all",
  "pending",
  "running",
  "deferred",
  "succeeded",
  "failed",
  "dead",
  "skipped",
];

export function RunsPage() {
  const [status, setStatus] = useState<RunStatus | "all">("all");
  const [taskId, setTaskId] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const query: Record<string, string | undefined> = { limit: "50" };
  if (status !== "all") query.status = status;
  if (taskId) query.taskId = taskId;
  if (cursor) query.cursor = cursor;

  const { data, error, loading } = useAsync(() => Api.listRuns(query), [status, taskId, cursor]);

  function nextPage() {
    if (data?.nextCursor) {
      setCursorStack((s) => [...s, cursor ?? ""]);
      setCursor(data.nextCursor);
    }
  }
  function prevPage() {
    setCursorStack((s) => {
      const copy = [...s];
      const prev = copy.pop();
      setCursor(prev || null);
      return copy;
    });
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-white">Runs</h1>

      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            className={`btn ${status === s ? "btn-primary" : ""}`}
            onClick={() => {
              setStatus(s);
              setCursor(null);
              setCursorStack([]);
            }}
          >
            {s}
          </button>
        ))}
        <input
          className="input ml-auto max-w-xs font-mono"
          placeholder="filter by task id…"
          value={taskId}
          onChange={(e) => {
            setTaskId(e.target.value);
            setCursor(null);
            setCursorStack([]);
          }}
        />
      </div>

      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorBox error={error} />
      ) : !data || data.data.length === 0 ? (
        <Empty>No runs match.</Empty>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full bg-surface">
              <thead>
                <tr>
                  <th className="th">Run</th>
                  <th className="th">Task</th>
                  <th className="th">Slot</th>
                  <th className="th">Attempt</th>
                  <th className="th">Trigger</th>
                  <th className="th">Status</th>
                  <th className="th">HTTP</th>
                  <th className="th">Duration</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((r) => (
                  <tr key={r.id} className="hover:bg-bg/50">
                    <td className="td">
                      <Link className="mono text-xs text-primary hover:underline" to={`/runs/${r.id}`}>
                        {r.id}
                      </Link>
                    </td>
                    <td className="td">
                      <Link className="mono text-xs text-primary hover:underline" to={`/tasks/${r.taskId}`}>
                        {r.taskId}
                      </Link>
                    </td>
                    <td className="td mono text-xs">{fmtTime(r.scheduledSlot)}</td>
                    <td className="td mono text-xs">{r.attempt}</td>
                    <td className="td mono text-xs text-muted">{r.trigger}</td>
                    <td className="td">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="td mono text-xs">{r.responseStatus ?? "—"}</td>
                    <td className="td mono text-xs">{fmtDuration(r.durationMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <button className="btn" disabled={cursorStack.length === 0} onClick={prevPage}>
              Previous
            </button>
            <button className="btn" disabled={!data.nextCursor} onClick={nextPage}>
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}